import React, { useEffect, useMemo, useState } from "react";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "../components/misc/Tabs";
import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
} from "../components/misc/Card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/misc/Table";
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "../components/misc/Pagination";
import { ChevronDown, ChevronUp, RefreshCw, DollarSign, TrendingUp, TrendingDown } from "lucide-react";
import SimpleTooltip from "../components/misc/SimpleTooltip";
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {
    HourlyMLSUsage,
    MLSUsageTableData,
    MonthlyOpenAIUsage,
    MonthlyRentcastUsage,
    OpenAIMonthlyData,
    OpenAIUsageResponse,
    OpenAIEnvironmentSpend,
    OpenAIActivityData,
    OpenAIUserData,
    PaginatedRentcastResponse,
    PaginatedUserUsageResponse,
    RentcastUsage,
} from "./types";
import { getAuthHeader } from "../utils/authHeader";
import axios from "axios";
import LoadingScreen from "../screens/LoadingScreen";
import { useIsMobile } from "../hooks/useIsMobile";
import ZapCapTab from "./zapcap-balance/ZapCapTab";

// Utility function to format timestamps to readable dates
const formatTimestamp = (timestamp: number, period: 'hourly' | 'daily' | 'monthly' | 'yearly'): string => {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "Invalid Date";
    
    if (period === 'hourly') {
        return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:00`;
    } else if (period === 'daily') {
        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    } else if (period === 'monthly') {
        return date.toLocaleString('default', { month: 'short', year: 'numeric' });
    } else {
        return date.getFullYear().toString();
    }
};

// Utility function to format year and month to a readable string
const formatYearMonth = (year: number, month: number): string => {
    const date = new Date(year, month - 1);
    return date.toLocaleString("default", { month: "short", year: "numeric" });
};

const Statistics = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [mlsUsage, setMlsUsage] = useState<MLSUsageTableData[]>([]);
    const [hourlyMlsUsage, setHourlyMlsUsage] = useState<HourlyMLSUsage | null>(
        null
    );
    const [mlsTimePeriod, setMlsTimePeriod] = useState<'hourly' | 'daily' | 'monthly' | 'yearly'>('hourly');
    const [activeTab, setActiveTab] = useState<string>('rentcast');
    const [mlsTablePage, setMlsTablePage] = useState(1);
    const [mlsTableLimit] = useState(50);
    const [mlsTableHasMore, setMlsTableHasMore] = useState(true);
    const [loadingMlsTable, setLoadingMlsTable] = useState(false);

    const [paginatedRentcastData, setPaginatedRentcastData] =
        useState<PaginatedRentcastResponse | null>(null);
    const [rentcastPage, setRentcastPage] = useState(1);
    const [rentcastLimit] = useState(10);
    const [expandedUserIds, setExpandedUserIds] = useState<Set<string>>(
        new Set()
    );

    // State for per-user detailed usage with infinite scroll
    const [userDetailedUsage, setUserDetailedUsage] = useState<
        Map<string, RentcastUsage[]>
    >(new Map());
    const [userUsagePage, setUserUsagePage] = useState<Map<string, number>>(
        new Map()
    );
    const [userUsageTotalPages, setUserUsageTotalPages] = useState<
        Map<string, number>
    >(new Map());
    const [userUsageLimit] = useState(10);
    const [loadingUserDetails, setLoadingUserDetails] = useState<Set<string>>(
        new Set()
    );

    const [monthlyRentcastUsage, setMonthlyRentcastUsage] = useState<
        MonthlyRentcastUsage[]
    >([]);

    const [monthlyOpenAIUsage, setMonthlyOpenAIUsage] = useState<
        MonthlyOpenAIUsage[]
    >([]);

    const [openaiUsageData, setOpenaiUsageData] = useState<OpenAIUsageResponse | null>(null);
    // MB-1403: environment spend (OpenAI project split) + expanded activity rows.
    const [envSpend, setEnvSpend] = useState<OpenAIEnvironmentSpend | null>(null);
    const [expandedActivities, setExpandedActivities] = useState<Set<string>>(
        new Set()
    );
    const [expandedOpenAIUserIds, setExpandedOpenAIUserIds] = useState<Set<string>>(
        new Set()
    );
    const isMobile = useIsMobile(500)

    const fetchUserDetailedUsage = async (
        userId: string,
        page: number = 1,
        append: boolean = false
    ) => {
        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        setLoadingUserDetails((prev) => new Set(prev).add(userId));

        try {
            const response = await axios.get<PaginatedUserUsageResponse>(
                `${backendUrl}/admin/rentcast-usage-by-user`,
                {
                    headers,
                    params: {
                        userId,
                        page,
                        limit: userUsageLimit,
                    },
                }
            );

            if (response.status === 200) {
                setUserDetailedUsage((prev) => {
                    const newMap = new Map(prev);
                    const existingData = prev.get(userId) || [];
                    newMap.set(
                        userId,
                        append
                            ? [...existingData, ...response.data.data]
                            : response.data.data
                    );
                    return newMap;
                });

                setUserUsageTotalPages((prev) => {
                    const newMap = new Map(prev);
                    newMap.set(userId, response.data.totalPages);
                    return newMap;
                });

                setUserUsagePage((prev) => {
                    const newMap = new Map(prev);
                    newMap.set(userId, page);
                    return newMap;
                });
            }
        } catch (err) {
            console.error("Error fetching user detailed usage:", err);
        } finally {
            setLoadingUserDetails((prev) => {
                const newSet = new Set(prev);
                newSet.delete(userId);
                return newSet;
            });
        }
    };

    const toggleUserExpansion = async (userId: string) => {
        const newExpanded = new Set(expandedUserIds);
        if (newExpanded.has(userId)) {
            newExpanded.delete(userId);
        } else {
            newExpanded.add(userId);
            // Fetch detailed usage if not already loaded
            if (!userDetailedUsage.has(userId)) {
                await fetchUserDetailedUsage(userId, 1, false);
            }
        }
        setExpandedUserIds(newExpanded);
    };

    const toggleActivityExpansion = (activity: string) => {
        setExpandedActivities((prev) => {
            const next = new Set(prev);
            if (next.has(activity)) next.delete(activity);
            else next.add(activity);
            return next;
        });
    };

    const toggleOpenAIUserExpansion = (userId: string) => {
        setExpandedOpenAIUserIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(userId)) {
                newSet.delete(userId);
            } else {
                newSet.add(userId);
            }
            return newSet;
        });
    };

    const loadMoreUserUsage = async (userId: string) => {
        const currentPage = userUsagePage.get(userId) || 1;
        const totalPages = userUsageTotalPages.get(userId) || 1;

        if (currentPage < totalPages && !loadingUserDetails.has(userId)) {
            await fetchUserDetailedUsage(userId, currentPage + 1, true);
        }
    };

    const fetchMlsTableData = async (period: 'hourly' | 'daily' | 'monthly' | 'yearly', page: number = 1, append: boolean = false) => {
        setLoadingMlsTable(true);
        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        try {
            const response = await axios.get(
                `${backendUrl}/admin/mls-usage/${period}`,
                {
                    headers,
                    params: {
                        page,
                        limit: mlsTableLimit,
                    },
                }
            );

            if (response.status === 200) {
                const newData = response.data?.data || [];
                setMlsUsage(prev => append ? [...prev, ...newData] : newData);
                setMlsTableHasMore(newData.length === mlsTableLimit);
                setMlsTablePage(page);
            }
        } catch (err) {
            console.error("Error fetching MLS table data", err);
        } finally {
            setLoadingMlsTable(false);
        }
    };

    const loadMoreMlsData = () => {
        if (!loadingMlsTable && mlsTableHasMore) {
            fetchMlsTableData(mlsTimePeriod, mlsTablePage + 1, true);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        try {
            // Fetch all data concurrently
            const [
                mlsRes,
                rentcastRes,
                monthlyRentcastRes,
                monthlyOpenaiRes,
                openaiUsageRes,
                envSpendRes,
            ] = await Promise.all([
                axios.get<HourlyMLSUsage>(`${backendUrl}/admin/mls-usage`, {
                    headers,
                }),
                axios.get<PaginatedRentcastResponse>(
                    `${backendUrl}/admin/rentcast-usage`,
                    {
                        headers,
                        params: {
                            page: rentcastPage,
                            limit: rentcastLimit,
                        },
                    }
                ),
                axios.get<MonthlyRentcastUsage[]>(
                    `${backendUrl}/admin/rentcast-monthly-usage`,
                    { headers }
                ),
                axios.get<MonthlyOpenAIUsage[]>(
                    `${backendUrl}/admin/openai-monthly-usage`,
                    { headers }
                ),
                axios.get<OpenAIUsageResponse>(
                    `${backendUrl}/admin/openai-usage`,
                    { headers }
                ),
                // MB-1403: environment split — tolerate failure/unconfigured.
                axios
                    .get<OpenAIEnvironmentSpend>(
                        `${backendUrl}/admin/openai-environment-spend`,
                        { headers }
                    )
                    .catch(() => null),
            ]);

            // Set data if responses are successful
            if (mlsRes.status === 200) {
                setHourlyMlsUsage(mlsRes.data);
            } else throw new Error("Failed to fetch MLS usage");

            if (rentcastRes.status === 200)
                setPaginatedRentcastData(rentcastRes.data);
            else throw new Error("Failed to fetch Rentcast usage");

            if (monthlyRentcastRes.status === 200)
                setMonthlyRentcastUsage(monthlyRentcastRes.data);
            else throw new Error("Failed to fetch monthly Rentcast usage");

            // OpenAI monthly data for chart
            if (monthlyOpenaiRes.status === 200)
                setMonthlyOpenAIUsage(monthlyOpenaiRes.data);
            else throw new Error("Failed to fetch monthly OpenAI usage");

            // OpenAI usage table data
            if (openaiUsageRes.status === 200) {
                // API already returns the correct structure with summary and users
                setOpenaiUsageData(openaiUsageRes.data);
            }
            else throw new Error("Failed to fetch OpenAI usage");

            // MB-1403: optional — null when unconfigured or unavailable.
            setEnvSpend(envSpendRes?.data ?? null);
        } catch (err: any) {
            console.error("Error fetching data:", err);
            setError(err.message || "An error occurred while fetching data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rentcastPage]);

    useEffect(() => {
        setMlsTablePage(1);
        setMlsTableHasMore(true);
        fetchMlsTableData(mlsTimePeriod, 1, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mlsTimePeriod]);

    // Prepare data for Rentcast chart
    const rentcastChartData = useMemo(
        () =>
            monthlyRentcastUsage
                .sort((a, b) => a.timestamp - b.timestamp)
                .map((item) => ({
                    month: new Date(item.timestamp).toLocaleString("default", {
                        month: "short",
                        year: "numeric",
                    }),
                    count: item.count,
                })),
        [monthlyRentcastUsage]
    );

    // Prepare data for OpenAI chart from monthly endpoint
    const openaiChartData = useMemo(() => {
        if (!monthlyOpenAIUsage || monthlyOpenAIUsage.length === 0) return [];

        return monthlyOpenAIUsage
            .sort((a, b) => {
                const dateA = new Date(a.year, a.month - 1);
                const dateB = new Date(b.year, b.month - 1);
                return dateA.getTime() - dateB.getTime();
            })
            .map(item => ({
                month: formatYearMonth(item.year, item.month),
                totalCost: item.totalCost,
            }));
    }, [monthlyOpenAIUsage]);

    // Prepare data for MLS chart by combining dataUsage and mediaUsage
    const mlsChartData = useMemo(() => {
        if (!hourlyMlsUsage) return [];

        const periodData = hourlyMlsUsage[mlsTimePeriod];
        if (!periodData) return [];

        // Combine dataUsage and mediaUsage based on timestamp
        const combinedData: { time: string; Data: number; Media: number }[] =
            periodData.dataUsage.map((dataItem) => {
                // Use 'timestamp' if available; else, fallback to 'createdAt'
                const timestamp = dataItem.timestamp || dataItem.createdAt || 0;
                const mediaItem = periodData.mediaUsage.find(
                    (item) => (item.timestamp || item.createdAt) === timestamp
                );
                return {
                    time: formatTimestamp(timestamp, mlsTimePeriod),
                    Data: dataItem.count,
                    Media: mediaItem ? mediaItem.count : 0,
                };
            });

        return combinedData;
    }, [hourlyMlsUsage, mlsTimePeriod]);

    return (
      <div className="flex flex-col h-full p-4 space-y-6">
        <h2 className="text-2xl font-bold text-primary">Statistics</h2>

        {loading && <LoadingScreen />}
        {error && <p className="text-red-500">Error: {error}</p>}

        {!loading && !error && (
          <>
            {/* Tabs with Graphs and Tables */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="rentcast">Rentcast Usage</TabsTrigger>
                <TabsTrigger value="mls">MLS Usage</TabsTrigger>
                <TabsTrigger value="openai">OpenAI Usage</TabsTrigger>
                <TabsTrigger value="zapcap">Zapcap Balance</TabsTrigger>
              </TabsList>

              {/* Rentcast Tab */}
              <TabsContent value="rentcast">
                <div className="space-y-6">
                  {/* Rentcast Usage Chart */}
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle>Monthly Rentcast Usage</CardTitle>
                      <SimpleTooltip
                        message="Refresh Rentcast usage data"
                        visible={true}
                        className="w-auto"
                      >
                        <button
                          onClick={fetchData}
                          disabled={loading}
                          className="p-2 rounded-md hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <RefreshCw
                            className={`h-5 w-5 ${loading ? "animate-spin" : ""}`}
                          />
                        </button>
                      </SimpleTooltip>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={400}>
                        <LineChart data={rentcastChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="month"
                            label={{
                              value: "Month",
                              position: "insideBottomRight",
                              offset: -5,
                            }}
                          />
                          <YAxis
                            label={{
                              value: "Usage Count",
                              angle: -90,
                              position: "insideLeft",
                            }}
                          />
                          <Tooltip />
                          <Legend verticalAlign="top" height={36} />
                          <Line
                            type="monotone"
                            dataKey="count"
                            name="Rentcast Usage"
                            stroke="#8884d8"
                            activeDot={{ r: 8 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Rentcast Usage Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Rentcast Usage Details</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[50px]"></TableHead>
                            <TableHead>User</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead className="text-right">
                              Total Usage
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginatedRentcastData?.data &&
                          paginatedRentcastData.data.length > 0 ? (
                            paginatedRentcastData.data.map((groupedUser) => {
                              const isExpanded = expandedUserIds.has(
                                groupedUser.user._id,
                              );
                              const displayName =
                                groupedUser.user.firstName &&
                                groupedUser.user.lastName
                                  ? `${groupedUser.user.firstName} ${groupedUser.user.lastName}`
                                  : groupedUser.user.email ||
                                    groupedUser.user._id;

                              return (
                                <React.Fragment key={groupedUser.user._id}>
                                  {/* Main User Row */}
                                  <TableRow
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() =>
                                      toggleUserExpansion(groupedUser.user._id)
                                    }
                                  >
                                    <TableCell>
                                      {isExpanded ? (
                                        <ChevronUp className="h-4 w-4" />
                                      ) : (
                                        <ChevronDown className="h-4 w-4" />
                                      )}
                                    </TableCell>
                                    <TableCell className="font-medium">
                                      {displayName}
                                    </TableCell>
                                    <TableCell>
                                      {groupedUser.user.email || "N/A"}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {groupedUser.totalUsageCount}
                                    </TableCell>
                                  </TableRow>

                                  {/* Expanded Usage Records */}
                                  {isExpanded && (
                                    <TableRow>
                                      <TableCell colSpan={4} className="p-0">
                                        <div className="bg-muted/30 p-4">
                                          {!userDetailedUsage.has(
                                            groupedUser.user._id,
                                          ) ? (
                                            <div className="flex justify-center py-8">
                                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                            </div>
                                          ) : (
                                            <div className="max-h-96 overflow-y-auto">
                                              <Table>
                                                <TableHeader className="sticky top-0 bg-background z-10">
                                                  <TableRow>
                                                    <TableHead>
                                                      Timestamp
                                                    </TableHead>
                                                    <TableHead>
                                                      Created At
                                                    </TableHead>
                                                    <TableHead>
                                                      Record ID
                                                    </TableHead>
                                                  </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                  {userDetailedUsage
                                                    .get(groupedUser.user._id)
                                                    ?.map((record) => (
                                                      <TableRow
                                                        key={record._id}
                                                      >
                                                        <TableCell>
                                                          {new Date(
                                                            record.timestamp,
                                                          ).toLocaleString()}
                                                        </TableCell>
                                                        <TableCell>
                                                          {new Date(
                                                            record.createdAt,
                                                          ).toLocaleString()}
                                                        </TableCell>
                                                        <TableCell className="font-mono text-xs">
                                                          {record._id}
                                                        </TableCell>
                                                      </TableRow>
                                                    ))}
                                                  {loadingUserDetails.has(
                                                    groupedUser.user._id,
                                                  ) && (
                                                    <TableRow>
                                                      <TableCell
                                                        colSpan={3}
                                                        className="text-center py-4"
                                                      >
                                                        <div className="flex justify-center items-center gap-2">
                                                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                                                          <span className="text-sm text-muted-foreground">
                                                            Loading more...
                                                          </span>
                                                        </div>
                                                      </TableCell>
                                                    </TableRow>
                                                  )}
                                                  {!loadingUserDetails.has(
                                                    groupedUser.user._id,
                                                  ) &&
                                                    userDetailedUsage.has(
                                                      groupedUser.user._id,
                                                    ) &&
                                                    (userDetailedUsage.get(
                                                      groupedUser.user._id,
                                                    )?.length || 0) > 0 && (
                                                      <>
                                                        {(userUsagePage.get(
                                                          groupedUser.user._id,
                                                        ) || 1) <
                                                          (userUsageTotalPages.get(
                                                            groupedUser.user
                                                              ._id,
                                                          ) || 1) && (
                                                          <TableRow>
                                                            <TableCell
                                                              colSpan={3}
                                                              className="text-center py-4"
                                                            >
                                                              <button
                                                                onClick={() =>
                                                                  loadMoreUserUsage(
                                                                    groupedUser
                                                                      .user._id,
                                                                  )
                                                                }
                                                                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors text-sm font-medium"
                                                              >
                                                                Load More
                                                              </button>
                                                            </TableCell>
                                                          </TableRow>
                                                        )}
                                                      </>
                                                    )}
                                                </TableBody>
                                              </Table>
                                            </div>
                                          )}
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </React.Fragment>
                              );
                            })
                          ) : (
                            <TableRow>
                              <TableCell colSpan={4} className="text-center">
                                No Rentcast Usage Data
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                    {paginatedRentcastData?.pagination &&
                      paginatedRentcastData.pagination.totalPages > 1 && (
                        <CardFooter>
                          <Pagination className="w-full">
                            <PaginationContent>
                              <PaginationItem>
                                <PaginationPrevious
                                  onClick={() =>
                                    setRentcastPage(
                                      Math.max(rentcastPage - 1, 1),
                                    )
                                  }
                                  className={
                                    rentcastPage <= 1
                                      ? "pointer-events-none opacity-50"
                                      : "cursor-pointer"
                                  }
                                />
                              </PaginationItem>

                              {Array.from(
                                {
                                  length:
                                    paginatedRentcastData.pagination.totalPages,
                                },
                                (_, i) => i + 1,
                              ).map((pageNum) => {
                                const showPage =
                                  pageNum === 1 ||
                                  pageNum ===
                                    paginatedRentcastData.pagination
                                      .totalPages ||
                                  Math.abs(pageNum - rentcastPage) <= 1;

                                if (!showPage) {
                                  if (
                                    pageNum === rentcastPage - 2 ||
                                    pageNum === rentcastPage + 2
                                  ) {
                                    return (
                                      <PaginationItem key={pageNum}>
                                        <PaginationEllipsis />
                                      </PaginationItem>
                                    );
                                  }
                                  return null;
                                }

                                return (
                                  <PaginationItem key={pageNum}>
                                    <PaginationLink
                                      onClick={() => setRentcastPage(pageNum)}
                                      isActive={rentcastPage === pageNum}
                                      className="cursor-pointer"
                                    >
                                      {pageNum}
                                    </PaginationLink>
                                  </PaginationItem>
                                );
                              })}

                              <PaginationItem>
                                <PaginationNext
                                  onClick={() =>
                                    setRentcastPage(
                                      Math.min(
                                        rentcastPage + 1,
                                        paginatedRentcastData.pagination
                                          .totalPages,
                                      ),
                                    )
                                  }
                                  className={
                                    rentcastPage >=
                                    paginatedRentcastData.pagination.totalPages
                                      ? "pointer-events-none opacity-50"
                                      : "cursor-pointer"
                                  }
                                />
                              </PaginationItem>
                            </PaginationContent>
                          </Pagination>
                        </CardFooter>
                      )}
                  </Card>
                </div>
              </TabsContent>

              {/* MLS Tab */}
              <TabsContent value="mls">
                <div className="space-y-6">
                  {/* MLS Usage Chart */}
                  <Card>
                    <CardHeader
                      className={`${isMobile ? "gap-y-4" : "gap-y-0"} grid grid-cols-12 items-center pb-2 `}
                    >
                      <CardTitle
                        className={`${isMobile ? "col-span-7 order-1 text-left flex items-center" : "col-span-7 text-left"} `}
                      >
                        MLS Usage -{" "}
                        {mlsTimePeriod.charAt(0).toUpperCase() +
                          mlsTimePeriod.slice(1)}
                      </CardTitle>

                      <div
                        className={`${isMobile ? "col-span-12 order-3 flex justify-center" : "col-span-4 flex gap-2 justify-end "} `}
                      >
                        <button
                          onClick={() => setMlsTimePeriod("hourly")}
                          className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                            mlsTimePeriod === "hourly"
                              ? "bg-primary text-white"
                              : "bg-muted hover:bg-muted/80"
                          }`}
                        >
                          Hourly
                        </button>
                        <button
                          onClick={() => setMlsTimePeriod("daily")}
                          className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                            mlsTimePeriod === "daily"
                              ? "bg-primary text-white"
                              : "bg-muted hover:bg-muted/80"
                          }`}
                        >
                          Daily
                        </button>
                        <button
                          onClick={() => setMlsTimePeriod("monthly")}
                          className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                            mlsTimePeriod === "monthly"
                              ? "bg-primary text-white"
                              : "bg-muted hover:bg-muted/80"
                          }`}
                        >
                          Monthly
                        </button>
                        <button
                          onClick={() => setMlsTimePeriod("yearly")}
                          className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                            mlsTimePeriod === "yearly"
                              ? "bg-primary text-white"
                              : "bg-muted hover:bg-muted/80"
                          }`}
                        >
                          Yearly
                        </button>
                      </div>

                      <div
                        className={`${isMobile ? "col-span-5 order-2 flex justify-end self-start" : "col-span-1 flex justify-end items-center "} `}
                      >
                        <SimpleTooltip
                          message="Refresh MLS usage data"
                          visible={true}
                          className="w-auto"
                        >
                          <button
                            onClick={fetchData}
                            disabled={loading}
                            className=" rounded-md hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <RefreshCw
                              className={`h-5 w-5 ${loading ? "animate-spin" : ""}`}
                            />
                          </button>
                        </SimpleTooltip>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={400}>
                        <LineChart data={mlsChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="time"
                            label={{
                              value: "Time",
                              position: "insideBottomRight",
                              offset: -5,
                            }}
                          />
                          <YAxis
                            label={{
                              value: "Usage Count",
                              angle: -90,
                              position: "insideLeft",
                            }}
                          />
                          <Tooltip />
                          <Legend verticalAlign="top" height={36} />
                          <Line
                            type="monotone"
                            dataKey="Data"
                            name="Data Usage"
                            stroke="#8884d8"
                            activeDot={{ r: 8 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="Media"
                            name="Media Usage"
                            stroke="#82ca9d"
                            activeDot={{ r: 8 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                  {/* MLS Usage Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle>
                        MLS Usage Details -{" "}
                        {mlsTimePeriod.charAt(0).toUpperCase() +
                          mlsTimePeriod.slice(1)}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="max-h-[600px] overflow-y-auto">
                        <table className="min-w-full table-auto">
                          <thead className="sticky top-0 bg-background z-10">
                            <tr>
                              <th className="px-4 py-2 border">Date</th>
                              <th className="px-4 py-2 border">Data Count</th>
                              <th className="px-4 py-2 border">Media Count</th>
                              <th className="px-4 py-2 border">Total Count</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mlsUsage && mlsUsage.length > 0
                              ? mlsUsage.map((item, index) => (
                                  <tr
                                    key={`${item.timestamp}-${index}`}
                                    className="text-center"
                                  >
                                    <td className="border px-4 py-2">
                                      {formatTimestamp(
                                        item.timestamp,
                                        mlsTimePeriod,
                                      )}
                                    </td>
                                    <td className="border px-4 py-2">
                                      {item.dataCount}
                                    </td>
                                    <td className="border px-4 py-2">
                                      {item.mediaCount}
                                    </td>
                                    <td className="border px-4 py-2">
                                      {item.totalCount}
                                    </td>
                                  </tr>
                                ))
                              : !loadingMlsTable && (
                                  <tr>
                                    <td
                                      colSpan={4}
                                      className="border px-4 py-2 text-center"
                                    >
                                      No MLS Usage Data
                                    </td>
                                  </tr>
                                )}
                            {loadingMlsTable && (
                              <tr>
                                <td
                                  colSpan={4}
                                  className="border px-4 py-4 text-center"
                                >
                                  <div className="flex justify-center items-center gap-2">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                                    <span className="text-sm text-muted-foreground">
                                      Loading...
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      {mlsTableHasMore &&
                        mlsUsage.length > 0 &&
                        !loadingMlsTable && (
                          <div className="flex justify-center mt-4">
                            <button
                              onClick={loadMoreMlsData}
                              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors text-sm font-medium"
                            >
                              Load More
                            </button>
                          </div>
                        )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* OpenAI Tab */}
              <TabsContent value="openai">
                <div className="space-y-6">
                  {/* Summary Statistics */}
                  {openaiUsageData?.summary && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold">
                          Usage Summary
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div
                          className={`${isMobile ? "grid grid-cols-2" : "grid grid-cols-3"} gap-4`}
                        >
                          <div
                            className="p-4 rounded-lg border
  bg-gradient-to-br from-green-50 to-emerald-50
  dark:from-green-950/20 dark:to-emerald-950/20
  border-green-200 dark:border-green-800"
                          >
                            {isMobile ? (
                              /* ---------- MOBILE LAYOUT ---------- */
                              <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                                <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                                  <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
                                </div>

                                <p className="text-xs font-medium text-muted-foreground self-center">
                                  Total Cost
                                </p>

                                <p className="col-span-2 text-lg font-bold text-green-700 dark:text-green-400 break-words">
                                  $
                                  {openaiUsageData.summary.totalCost.toFixed(2)}
                                </p>
                              </div>
                            ) : (
                              /* ---------- DESKTOP LAYOUT ---------- */
                              <div className="flex items-start space-x-3">
                                <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                                  <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
                                </div>

                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-muted-foreground mb-1">
                                    Total Cost
                                  </p>

                                  <p className="text-2xl font-bold text-green-700 dark:text-green-400 break-words">
                                    $
                                    {openaiUsageData.summary.totalCost.toFixed(
                                      2,
                                    )}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>

                          <div
                            className="p-4 rounded-lg border
  bg-gradient-to-br from-blue-50 to-cyan-50
  dark:from-blue-950/20 dark:to-cyan-950/20
  border-blue-200 dark:border-blue-800"
                          >
                            {isMobile ? (
                              /* ---------- MOBILE ---------- */
                              <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                                <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                                  <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                </div>

                                <p className="text-xs font-medium text-muted-foreground self-center">
                                  Input Tokens
                                </p>

                                <p className="col-span-2 text-lg font-bold text-blue-700 dark:text-blue-400 break-words">
                                  {openaiUsageData.summary.totalInputTokens.toLocaleString()}
                                </p>
                              </div>
                            ) : (
                              /* ---------- DESKTOP ---------- */
                              <div className="flex items-start space-x-3">
                                <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                                  <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                </div>

                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-muted-foreground mb-1">
                                    Input Tokens
                                  </p>

                                  <p className="text-2xl font-bold text-blue-700 dark:text-blue-400 break-words">
                                    {openaiUsageData.summary.totalInputTokens.toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>

                          <div
                            className="p-4 rounded-lg border
  bg-gradient-to-br from-purple-50 to-pink-50
  dark:from-purple-950/20 dark:to-pink-950/20
  border-purple-200 dark:border-purple-800"
                          >
                            {isMobile ? (
                              /* ---------- MOBILE ---------- */
                              <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                                <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                                  <TrendingDown className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                                </div>

                                <p className="text-xs font-medium text-muted-foreground self-center">
                                  Output Tokens
                                </p>

                                <p className="col-span-2 text-lg font-bold text-purple-700 dark:text-purple-400 break-words">
                                  {openaiUsageData.summary.totalOutputTokens.toLocaleString()}
                                </p>
                              </div>
                            ) : (
                              /* ---------- DESKTOP ---------- */
                              <div className="flex items-start space-x-3">
                                <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                                  <TrendingDown className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                                </div>

                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-muted-foreground mb-1">
                                    Output Tokens
                                  </p>

                                  <p className="text-2xl font-bold text-purple-700 dark:text-purple-400 break-words">
                                    {openaiUsageData.summary.totalOutputTokens.toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* OpenAI Usage Chart */}
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle>Monthly OpenAI Usage</CardTitle>
                      <SimpleTooltip
                        message="Refresh OpenAI usage data"
                        visible={true}
                        className="w-auto"
                      >
                        <button
                          onClick={fetchData}
                          disabled={loading}
                          className="p-2 rounded-md hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <RefreshCw
                            className={`h-5 w-5 ${loading ? "animate-spin" : ""}`}
                          />
                        </button>
                      </SimpleTooltip>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={400}>
                        <LineChart data={openaiChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="month"
                            label={{
                              value: "Month",
                              position: "insideBottomRight",
                              offset: -5,
                            }}
                          />
                          <YAxis
                            label={{
                              value: "Total Cost ($)",
                              angle: -90,
                              position: "insideLeft",
                            }}
                            domain={[0, "auto"]}
                          />
                          <Tooltip />
                          <Legend verticalAlign="top" height={36} />
                          <Line
                            type="monotone"
                            dataKey="totalCost"
                            name="Total Cost"
                            stroke="#ffc658"
                            activeDot={{ r: 8 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* MB-1403: environment spend from OpenAI billing (project keys) */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base font-semibold">
                        Environment Spend (OpenAI billing, last{" "}
                        {envSpend?.days ?? 30} days)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {envSpend?.configured && !envSpend.error ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Environment / Project</TableHead>
                              <TableHead className="text-right">
                                Spend ($)
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {envSpend.projects.map((project) => (
                              <TableRow key={project.projectId}>
                                <TableCell className="font-medium">
                                  {project.name}
                                </TableCell>
                                <TableCell className="text-right">
                                  ${project.amountUsd.toFixed(2)}
                                </TableCell>
                              </TableRow>
                            ))}
                            <TableRow>
                              <TableCell className="font-semibold">
                                Total
                              </TableCell>
                              <TableCell className="text-right font-semibold">
                                ${envSpend.totalUsd.toFixed(2)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      ) : envSpend && !envSpend.configured ? (
                        <p className="text-sm text-muted-foreground">
                          Environment split not configured yet — it lights up
                          when the DEV/QA project keys and the OPENAI_ADMIN_KEY
                          secret land with the next deployment. Until then this
                          dashboard reflects PROD-recorded usage only.
                        </p>
                      ) : (
                        // MB-1403: configured-but-failing, or the request itself
                        // failed. Must NOT reuse the "not configured yet" copy —
                        // that tells an admin to wait for a rollout that already
                        // happened while the real cause is a broken key.
                        <p className="text-sm text-red-500">
                          {envSpend?.error ??
                            "Environment spend could not be loaded — the request to the API failed."}
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {/* MB-1403: spend by activity */}
                  {openaiUsageData?.activities &&
                    openaiUsageData.activities.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle>Spend by Activity</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-[50px]"></TableHead>
                                <TableHead>Activity</TableHead>
                                <TableHead className="text-right">
                                  Total Cost ($)
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {openaiUsageData.activities.map(
                                (activityRow: OpenAIActivityData) => {
                                  const isExpanded = expandedActivities.has(
                                    activityRow.activity,
                                  );
                                  return (
                                    <React.Fragment key={activityRow.activity}>
                                      <TableRow
                                        className="cursor-pointer hover:bg-muted/50"
                                        onClick={() =>
                                          toggleActivityExpansion(
                                            activityRow.activity,
                                          )
                                        }
                                      >
                                        <TableCell>
                                          {isExpanded ? (
                                            <ChevronUp className="h-4 w-4" />
                                          ) : (
                                            <ChevronDown className="h-4 w-4" />
                                          )}
                                        </TableCell>
                                        <TableCell className="font-medium">
                                          {activityRow.activity}
                                        </TableCell>
                                        <TableCell className="text-right">
                                          ${activityRow.totalCost.toFixed(2)}
                                        </TableCell>
                                      </TableRow>
                                      {isExpanded && (
                                        <TableRow>
                                          <TableCell
                                            colSpan={3}
                                            className="p-0"
                                          >
                                            <div className="bg-muted/30 p-4">
                                              <Table>
                                                <TableHeader>
                                                  <TableRow>
                                                    <TableHead>Month</TableHead>
                                                    <TableHead className="text-right">
                                                      Input Tokens
                                                    </TableHead>
                                                    <TableHead className="text-right">
                                                      Output Tokens
                                                    </TableHead>
                                                    <TableHead className="text-right">
                                                      Cost ($)
                                                    </TableHead>
                                                  </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                  {activityRow.monthlyData.map(
                                                    (month) => (
                                                      <TableRow
                                                        key={`${month.year}-${month.month}`}
                                                      >
                                                        <TableCell>
                                                          {month.year}-
                                                          {String(
                                                            month.month,
                                                          ).padStart(2, "0")}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                          {month.totalInputTokens.toLocaleString()}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                          {month.totalOutputTokens.toLocaleString()}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                          $
                                                          {month.totalCost.toFixed(
                                                            2,
                                                          )}
                                                        </TableCell>
                                                      </TableRow>
                                                    ),
                                                  )}
                                                </TableBody>
                                              </Table>
                                            </div>
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </React.Fragment>
                                  );
                                },
                              )}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    )}

                  {/* OpenAI Usage Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle>OpenAI Usage Details</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[50px]"></TableHead>
                            <TableHead>First Name</TableHead>
                            <TableHead>Last Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead className="text-right">
                              Total Cost ($)
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {openaiUsageData?.users &&
                          openaiUsageData.users.length > 0 ? (
                            openaiUsageData.users.map(
                              (user: OpenAIUserData) => {
                                const isExpanded = expandedOpenAIUserIds.has(
                                  user.userId,
                                );

                                return (
                                  <React.Fragment key={user.userId}>
                                    {/* Main User Row */}
                                    <TableRow
                                      className="cursor-pointer hover:bg-muted/50"
                                      onClick={() =>
                                        toggleOpenAIUserExpansion(user.userId)
                                      }
                                    >
                                      <TableCell>
                                        {isExpanded ? (
                                          <ChevronUp className="h-4 w-4" />
                                        ) : (
                                          <ChevronDown className="h-4 w-4" />
                                        )}
                                      </TableCell>
                                      <TableCell className="font-medium">
                                        {user.firstName || "-"}
                                      </TableCell>
                                      <TableCell className="font-medium">
                                        {user.lastName || "-"}
                                      </TableCell>
                                      <TableCell>
                                        {user.email || "-"}
                                        {user.deleted && (
                                          <span className="ml-1 text-xs text-muted-foreground">
                                            (deleted)
                                          </span>
                                        )}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        ${user.totalCost.toFixed(2)}
                                      </TableCell>
                                    </TableRow>

                                    {/* Expanded Monthly Data */}
                                    {isExpanded && (
                                      <TableRow>
                                        <TableCell colSpan={5} className="p-0">
                                          <div className="bg-muted/30 p-4">
                                            <div className="max-h-96 overflow-y-auto">
                                              <Table>
                                                <TableHeader className="sticky top-0 bg-background z-10">
                                                  <TableRow>
                                                    <TableHead>Month</TableHead>
                                                    <TableHead className="text-right">
                                                      Input Tokens
                                                    </TableHead>
                                                    <TableHead className="text-right">
                                                      Output Tokens
                                                    </TableHead>
                                                    <TableHead className="text-right">
                                                      Cost ($)
                                                    </TableHead>
                                                  </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                  {user.monthlyData
                                                    .sort(
                                                      (
                                                        a: OpenAIMonthlyData,
                                                        b: OpenAIMonthlyData,
                                                      ) => {
                                                        const dateA = new Date(
                                                          a.year,
                                                          a.month - 1,
                                                        );
                                                        const dateB = new Date(
                                                          b.year,
                                                          b.month - 1,
                                                        );
                                                        return (
                                                          dateB.getTime() -
                                                          dateA.getTime()
                                                        );
                                                      },
                                                    )
                                                    .map(
                                                      (
                                                        monthData: OpenAIMonthlyData,
                                                        idx: number,
                                                      ) => (
                                                        <TableRow
                                                          key={`${monthData.year}-${monthData.month}-${idx}`}
                                                        >
                                                          <TableCell>
                                                            {formatYearMonth(
                                                              monthData.year,
                                                              monthData.month,
                                                            )}
                                                          </TableCell>
                                                          <TableCell className="text-right">
                                                            {monthData.totalInputTokens.toLocaleString()}
                                                          </TableCell>
                                                          <TableCell className="text-right">
                                                            {monthData.totalOutputTokens.toLocaleString()}
                                                          </TableCell>
                                                          <TableCell className="text-right">
                                                            $
                                                            {monthData.totalCost.toFixed(
                                                              2,
                                                            )}
                                                          </TableCell>
                                                        </TableRow>
                                                      ),
                                                    )}
                                                </TableBody>
                                              </Table>
                                            </div>
                                          </div>
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </React.Fragment>
                                );
                              },
                            )
                          ) : (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center">
                                No OpenAI Usage Data
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* ZapCap Tab */}
              <TabsContent value="zapcap">
                <ZapCapTab />
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    );
};

export default Statistics;
